<script>
  import { format } from 'svelte-i18n';
  import Prism from 'svelte-prismjs';
  import { Button, Modal, ModalBody } from '@sveltestrap/sveltestrap';

  import { Clipboard } from '@client/components/clipboard';
  import { Icon } from '@client/components/icon';
  import { Select } from '@client/components/select';

  import { DomainsService } from '@client/services/domains';

  import { isValidDomain } from '@common/utils/validation';

  export let domainname = 'example.com';
  export let internaldomains = [];
  export let show = false;

  let copyText = 'label.COPY';

  let billingDomains = [];

  let domainValue = [];
  let domainFilterText = '';
  let groupValue = '';
  let initialized = false;

  // Build a Select options list, injecting a normalized + validated free-entry option (svelte-select v5 has no creatable)
  const buildOptions = (source, selectedValues, filterText, { validate, normalize } = {}) => {
    const selected = new Set(selectedValues);
    const options = Array.from(new Set(source))
      .filter((value) => value && !selected.has(value))
      .map((value) => ({ value, label: value }));

    const raw = (filterText || '').trim();
    const typed = normalize ? normalize(raw) : raw;

    if (
      typed &&
      (!validate || validate(typed)) &&
      !selected.has(typed) &&
      !options.some((option) => option.value === typed)
    ) {
      options.unshift({ value: typed, label: typed });
    }

    return options;
  };

  // Same-billing-id domains, resolved from the current domain's billing id (mirrors the domain-aliases lookup)
  const fetchBillingDomains = async () => {
    try {
      const current = await DomainsService.getAll({ query: domainname, page: 1, perPage: 1, screen: 'staff' });
      const domainSettings = current?.data?.[0]?.domainsettings;
      const billingId = domainSettings?.billing_id || domainSettings?.billing_customer_id;

      if (!billingId) return [];

      const { data } = await DomainsService.getAll({
        billingid: billingId,
        page: 1,
        perPage: 1000,
        query: '',
        screen: 'staff'
      });

      return (data || []).map(({ domainname: name }) => name).filter(Boolean);
    } catch (error) {
      console.error('Error fetching billing ID domains:', error);
      return [];
    }
  };

  // Seed the primary domain and load options once settings provide the real domain name
  $: if (!initialized && domainname && domainname !== 'example.com') {
    initialized = true;
    domainValue = [{ value: domainname, label: domainname }];
    buildScript(domainValue, groupValue);
    fetchBillingDomains().then((domainsList) => {
      billingDomains = domainsList;
    });
  }

  $: domainItems = buildOptions(
    [domainname, ...billingDomains, ...(internaldomains || [])],
    (domainValue || []).map((domain) => domain.value),
    domainFilterText,
    { validate: isValidDomain, normalize: (value) => value.toLowerCase() }
  );

  // Rebuild the script live as the group name is typed (accepted verbatim, no validation)
  const onGroupInput = (event) => {
    buildScript(domainValue, event.target.value);
  };

  const getScriptContent = (domainList, group) => {
    const domainLine = domainList.length ? domainList.map((domain) => `"${domain}"`).join(', ') : '"example.com"';
    const groupValue = group || 'group';

    return `
### BEGIN SENDERGEN SETUP SCRIPT ###

# Define variables
$domain = ${domainLine} ### Enter domains used for Opensense; separate multiple "domain1.com", "domain2.com", etc.
$groupName = "${groupValue}" ### Match the group name created during integration
$OutConnectorName = "Opensense Outbound Connector"
$InConnectorName = "Opensense Inbound Connector"

# Function to check if an Outbound Connector exists
function Test-OutboundConnector {
  param ($ConnectorName)
  return Get-OutboundConnector -ErrorAction SilentlyContinue | Where-Object { $_.Name -eq $ConnectorName }
}

# Function to check if an Inbound Connector exists
function Test-InboundConnector {
  param ($ConnectorName)
  return Get-InboundConnector -ErrorAction SilentlyContinue | Where-Object { $_.Name -eq $ConnectorName }
}

# Function to check if a Transport Rule exists
function Test-TransportRule {
  param ($RuleName)
  return Get-TransportRule -ErrorAction SilentlyContinue | Where-Object { $_.Name -eq $RuleName }
}

# Create Outbound Connector if not exists
if (-not (Test-OutboundConnector -ConnectorName $OutConnectorName)) {
  New-OutboundConnector -Name $OutConnectorName -SmartHosts "office365-api.sendergen.com" -IsTransportRuleScoped $true -UseMXRecord $false -TlsSettings "CertificateValidation" -ConnectorType "OnPremises" -CloudServicesMailEnabled $true | Out-Null
  Write-Output "Created Outbound Connector: $OutConnectorName"
} else {
  Write-Output "Outbound Connector '$OutConnectorName' already exists."
}

# Create Inbound Connector if not exists
if (-not (Test-InboundConnector -ConnectorName $InConnectorName)) {
  New-InboundConnector -Name $InConnectorName -RequireTLS $true -TlsSenderCertificateName "*.sendergen.com" -ConnectorType "OnPremises" -SenderDomains * -CloudServicesMailEnabled $true | Out-Null
  Write-Output "Created Inbound Connector: $InConnectorName"
} else {
  Write-Output "Inbound Connector '$InConnectorName' already exists."
}

# Validate Outbound Connector
Set-OutboundConnector -Identity $OutConnectorName -IsValidated $true -LastValidationTimestamp (Get-Date).ToUniversalTime()
Write-Output "Outbound Connector validated."

# Create Transport Rules if not exists
if (-not (Test-TransportRule -RuleName "Opensense Exclude Calendar")) {
  New-TransportRule -Name "Opensense Exclude Calendar" -FromScope InOrganization -SenderDomainIs $domain -MessageTypeMatches "Calendaring" -SetHeaderName "X-SG-Signature" -SetHeaderValue "true" -SenderAddressLocation "Envelope" -StopRuleProcessing $false -Priority "0" | Out-Null
  Write-Output "Created Transport Rule: Opensense Exclude Calendar"
}

if (-not (Test-TransportRule -RuleName "Opensense Enabled")) {
  New-TransportRule -Name "Opensense Enabled" -FromScope InOrganization -RouteMessageOutboundConnector $OutConnectorName -SenderDomainIs $domain -SubjectOrBodyMatchesPatterns "\\[\\[\\#\\]\\]" -ExceptIfMessageTypeMatches "OOF" -ExceptIfHeaderMatchesMessageHeader "X-SG-Signature" -ExceptIfHeaderMatchesPatterns "true" -ExceptIfFromAddressMatchesPatterns "\\.onmicrosoft\\.com","^$","^noreply_skype_voicemail",".\\.salesforce\\.com" -SenderAddressLocation "Envelope" -StopRuleProcessing $true -Priority "1" | Out-Null
  Write-Output "Created Transport Rule: Opensense Enabled"
}

if (-not (Test-TransportRule -RuleName "Opensense Stamping")) {
  New-TransportRule -Name "Opensense Stamping" -FromScope InOrganization -RouteMessageOutboundConnector $OutConnectorName -SenderDomainIs $domain -FromMemberOf $groupName -ApplyHtmlDisclaimerText "[[#]]<br>
name: %%DisplayName%%<br>
title:  %%Title%%<br>
phone:  %%Phone%%<br>
email:  %%Email%%<br>
mobile: %%MobileNumber%%<br>
[[#]]<br>" -ApplyHtmlDisclaimerLocation "Append" -ApplyHtmlDisclaimerFallbackAction "Ignore" -SetHeaderName "X-SG-Stamp" -SetHeaderValue "true" -ExceptIfMessageTypeMatches "OOF" -ExceptIfHeaderMatchesMessageHeader "X-SG-Signature" -ExceptIfHeaderMatchesPatterns "true" -ExceptIfFromAddressMatchesPatterns "\\.onmicrosoft\\.com","^$","^noreply_skype_voicemail",".\\.salesforce\\.com" -SenderAddressLocation "Envelope" -StopRuleProcessing $true -Priority "2" -Enabled $false | Out-Null
    Write-Output "Created Transport Rule: Opensense Stamping"
}

# Prevent winmail.dat legacy issues (Recommended by Microsoft)
Set-RemoteDomain Default -TNEFEnabled $false
Write-Output "Set-RemoteDomain Default -TNEFEnabled $false applied."

### END SETUP SCRIPT ###`;
  };

  const onCopy = () => {
    copyText = 'label.COPIED';

    setTimeout(() => {
      copyText = 'label.COPY';
    }, 1200);
  };

  const onClose = () => (show = false);

  const downloadScript = (text) => {
    const filename = 'M365_Integration_Script.ps1';
    const blob = new Blob([scriptContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  let scriptContent = getScriptContent([], undefined);

  // svelte-select churns value/items/filterText on selection, and that re-entrant flush swallows
  // the reactive DOM update for the generated script. Rebuild it directly from the select events
  // instead, taking the changed side from event.detail (authoritative — the bound value can be
  // stale mid-event, e.g. on individual chip removal) and the unchanged side from current state.
  const buildScript = (domainList, group) => {
    const domains = (domainList || []).map((domain) => domain.value.trim()).filter(Boolean);
    scriptContent = getScriptContent(domains, group?.trim());
  };
</script>

<Modal isOpen={show} fade={false} size="lg">
  <ModalBody>
    <div class="modal-body-inner">
      <button class="close" on:click={onClose}>
        <Icon name="x" />
      </button>
      <h2>{$format('label.POWERSHELL_SETUP_FOR_MICROSOFT_365')}</h2>
      <b>{$format('label.PREREQUISITES_FOR_POWERSHELL')}</b>
      <p>
        {@html $format('label.INSTALL_AZURE_AD_POWERSHELL_MODULE')}
      </p>

      <div class="script-config">
        <p class="help">{$format('label.POWERSHELL_DOMAINS_HELP')}</p>

        <div class="field">
          <span class="field-label">{$format('label.DOMAINS')}</span>
          <Select
            multiple
            searchable
            clearable
            items={domainItems}
            bind:value={domainValue}
            bind:filterText={domainFilterText}
            placeholder={$format('label.PLACEHOLDER.DOMAIN')}
            on:input={(event) => buildScript(event.detail, groupValue)}
            on:clear={() => buildScript([], groupValue)}
          >
            <svelte:fragment slot="empty">
              <div class="empty">{$format('label.NO_OPTIONS_AVAILABLE')}</div>
            </svelte:fragment>
          </Select>
        </div>

        <div class="field">
          <span class="field-label">{$format('label.GROUP_NAME')}</span>
          <input
            type="text"
            class="group-input"
            bind:value={groupValue}
            placeholder={$format('label.PLACEHOLDER.GROUP_NAME')}
            on:input={onGroupInput}
          />
        </div>
      </div>

      <h4 class="d-flex justify-content-between align-items-center">
        {$format('label.POWERSHELL_SCRIPT')}
        <div>
          <Clipboard text={scriptContent} let:copy on:copy={onCopy}>
            <Button size="sm" kind="secondary" on:click={copy} class="copy-to-clipboard">
              {$format(copyText)}
            </Button>
          </Clipboard>
          <Button size="sm" kind="secondary" on:click={downloadScript}>
            {$format('label.DOWNLOAD')}
          </Button>
        </div>
      </h4>

      <div class="snippet">
        {#key scriptContent}
          <Prism showLineNumbers={true} code={scriptContent} />
        {/key}
      </div>
    </div>
  </ModalBody>
</Modal>

<style lang="scss">
  .modal-body-inner {
    padding: var(--padding-large) var(--padding-large) var(--padding-xxxlarge);
    position: relative;

    p {
      margin-top: var(--margin-base);
    }

    ol {
      li {
        padding: var(--padding-small) 0;
      }
    }

    .close {
      background-color: transparent;
      border: 0;
      padding: 0;
      position: absolute;
      right: 0;
      top: 0;
      color: var(--color-gray-500);
      font-size: var(--font-size-xxlarge);

      &:hover {
        color: var(--color-gray-800);
      }
    }

    .script-config {
      margin-top: var(--margin-large);

      .help {
        color: var(--color-gray-600);
        margin-bottom: var(--margin-base);
      }

      .field {
        margin-bottom: var(--margin-large);

        .field-label {
          display: block;
          font-weight: var(--font-weight-medium);
          margin-bottom: var(--margin-small);
        }

        .group-input {
          background: var(--color-white);
          border: var(--input-border);
          border-radius: var(--input-border-radius);
          box-sizing: border-box;
          font-size: var(--input-font-size);
          height: var(--input-height);
          padding: var(--input-padding);
          width: 100%;

          &:focus {
            border: var(--input-border-focus);
            box-shadow: var(--input-box-shadow-focus);
            outline: none;
          }
        }
      }

      .empty {
        color: var(--color-gray-600);
        padding: var(--padding-xlarge);
        text-align: center;
      }
    }

    .snippet {
      margin-bottom: var(--margin-xlarge);
      position: relative;

      .copy-to-clipboard {
        background: var(--color-blue-400);
        border: 0;
        border-radius: 0 var(--border-radius-base) 0 var(--border-radius-base);
        color: var(--color-white);
        font-size: var(--font-size-medium);
        padding: var(--padding-base) var(--padding-large);
        position: absolute;
        right: 0;
        top: 0;
        z-index: 10;

        &:hover {
          box-shadow: inset 0 0 50px rgba(0, 0, 0, 0.1);
        }
      }
    }
  }
</style>
